import React, { useCallback } from 'react';

import { MenuTile } from 'features/menu/components/menu-tile-grid/menu-tile';
import {
  AnyMenuObject,
  getCaloriesAndPrice,
} from 'features/menu/components/menu-tile-grid/menu-tile/helpers';
import { useCalories } from 'features/menu/components/menu-tile-grid/menu-tile/hooks';
import { MainMenuObject } from 'features/menu/components/menu-tile-grid/menu-tile/types';
import { Separator } from 'features/menu/components/menu-tile-grid/separator';
import {
  StyledItemPrice,
  StyledListItemCalories,
  StyledMenuSectionTitle,
  StyledPriceAndCaloriesListWrapper,
  TileCard,
  TileFooter,
  TileImage,
} from 'features/menu/components/menu-tile-grid/styled';
import { IMenuTileGridProps } from 'features/menu/components/menu-tile-grid/types';
import { useGetVariantContentOptions } from 'features/menu/hooks/use-get-variant-content-options';
import { FlatList, ListRenderItemInfo, StyleSheet } from 'react-native';

import Link from 'components/link';
import { Calories } from 'components/price-and-calories';
import { Typography } from 'components/ui';
import { centeredRowContainerStyles } from 'components/ui/centered-row-container';
import { OtherAvailableOptionsHeader } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-section/other-available-options-header';
import {
  getFirstAvailableMenuItemIndex,
  shouldShowOtherAvailableOptionsHeader,
} from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/sort-menu-section-items';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { spacing } from 'styles/constants';
import { optionToUrl } from 'utils/menu';

// OLD (not one menu) — web
const MenuTileGrid = React.memo<IMenuTileGridProps>(
  ({
    data = [],
    gridTitle,
    pullToReloadProps,
    footerSpacer,
    objectFitContain = false,
    deeplinkUnavailableItemId,
    onUnavailableChooseRestaurant,
  }) => {
    const getVariantContentOptions = useGetVariantContentOptions();
    const isMobile = useIsMobileBreakpoint();
    // Unavailable deeplink recovery uses list chrome (badge/footer/CTA). Keep that
    // layout on desktop web too — the desktop grid Link/card path has no recovery UI.
    const useUnavailableListLayout = Boolean(deeplinkUnavailableItemId);
    const useListLayout = isMobile || useUnavailableListLayout;
    const getCalories = useCalories();
    const showOtherAvailableHeader = shouldShowOtherAvailableOptionsHeader(
      data,
      deeplinkUnavailableItemId
    );
    const firstAvailableIndex = getFirstAvailableMenuItemIndex(data, deeplinkUnavailableItemId);

    const renderItem = useCallback(
      ({ item, index }: ListRenderItemInfo<MainMenuObject>) => {
        const isUnavailable = Boolean(
          deeplinkUnavailableItemId && item._id === deeplinkUnavailableItemId
        );

        if (useListLayout) {
          return (
            <>
              {showOtherAvailableHeader && index === firstAvailableIndex ? (
                <OtherAvailableOptionsHeader />
              ) : null}
              <MenuTile
                objectFitContain={objectFitContain || item._type !== 'section'}
                item={item}
                deeplinkUnavailable={isUnavailable}
                onUnavailableChooseRestaurant={
                  isUnavailable ? onUnavailableChooseRestaurant : undefined
                }
              />
            </>
          );
        }

        const { image, name } = getVariantContentOptions(item);
        const linkPath = optionToUrl(item);
        const { calories, price } = getCaloriesAndPrice(
          item,
          getCalories as (menuObject: AnyMenuObject) => string
        );
        const isMenuItem = item._type !== 'section';

        return (
          <TileCard>
            <Link
              style={{
                width: '100%',
                height: '100%',
                flexDirection: 'column',
                alignItems: 'center',
              }}
              linkPath={linkPath}
              accessibilityLevel={3}
              focusable={true}
              accessibilityRole="header"
              accessible={true}
            >
              <TileImage
                image={image}
                alt={item.imageDescription?.locale ?? 'menu item image'}
                objectFitContain={isMenuItem}
                importantForAccessibility="no"
              />
              <TileFooter isMenuItem={isMenuItem}>
                <StyledMenuSectionTitle>{name?.locale}</StyledMenuSectionTitle>
                {isMenuItem ? (
                  <StyledPriceAndCaloriesListWrapper>
                    <StyledItemPrice>{price}</StyledItemPrice>
                    <Calories>
                      <StyledListItemCalories>{calories}</StyledListItemCalories>
                    </Calories>
                  </StyledPriceAndCaloriesListWrapper>
                ) : null}
              </TileFooter>
            </Link>
          </TileCard>
        );
      },
      [
        deeplinkUnavailableItemId,
        firstAvailableIndex,
        getCalories,
        getVariantContentOptions,
        objectFitContain,
        onUnavailableChooseRestaurant,
        showOtherAvailableHeader,
        useListLayout,
      ]
    );

    return (
      <FlatList
        testID="menu-tile-grid"
        contentContainerStyle={[
          centeredRowContainerStyles.content,
          { alignSelf: 'center' },
          useUnavailableListLayout ? styles.unavailableFeedContent : null,
        ]}
        key={useListLayout ? 'list' : 'desktop-grid'}
        data={data}
        {...pullToReloadProps}
        ListHeaderComponent={
          gridTitle ? <Typography variant="headerThree">{gridTitle}</Typography> : null
        }
        ItemSeparatorComponent={useListLayout ? Separator : null}
        ListFooterComponent={
          useListLayout ? (
            <>
              <Separator />
              {footerSpacer}
            </>
          ) : null
        }
        numColumns={useListLayout ? 1 : 3}
        showsVerticalScrollIndicator={false}
        keyExtractor={mainMenuObject => mainMenuObject._id}
        renderItem={renderItem}
      />
    );
  }
);

const styles = StyleSheet.create({
  unavailableFeedContent: {
    paddingTop: spacing.$2,
  },
});

export default MenuTileGrid;
